// frontend/src/hooks/useTrialCta.js
// PRD-193 WP4 — the ONE definition of the public trial CTA.
//
// The defect this fixes: every in-body guide CTA called bare
// `instance.loginRedirect(loginRequest)`, which lands a cold organic reader on the CIAM
// sign-IN form -- for an account they do not have. PublicHeader.js had been fixed to pass
// `prompt: 'create'` (PRD-171 §A8) but the fix stopped at the header, leaving 20 files with
// their own duplicated `handleLogin`. Twenty copies is why it silently drifted out of sync,
// so the remedy is not "fix twenty copies" but "have one".
//
// Every public trial CTA must route through this hook. If you find yourself writing
// `loginRedirect` in a page component, that is the bug reappearing.

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import { normalizeRoute } from '../services/telemetryService';

// react-snap pre-render guard — mirrors telemetryService.js exactly, so no analytics fire
// while react-snap captures static HTML.
const isPrerendering =
  typeof navigator !== 'undefined' &&
  (navigator.userAgent.includes('HeadlessChrome') ||
    navigator.userAgent.includes('ReactSnap'));

/**
 * Emit a GA4 `cta_click`.
 *
 * GA4 via `window.gtag` ONLY, and this is an architectural constraint rather than a
 * preference: the first-party path is unavailable to anonymous visitors by design.
 * `telemetryService.track()` returns early when unauthenticated and
 * `POST /api/telemetry-track` is `requireAuth` with the userId derived server-side from the
 * token. Emitting anonymous first-party events would mean breaking that trust model.
 *
 * Do NOT reach for `utils/onboardingAnalytics.js`'s `dataLayer.push` path -- the site runs
 * gtag.js with no GTM container, so those events are inert.
 *
 * Payload carries the normalized route and a CTA identifier only. Never a domain, email,
 * asset value, or raw URL.
 */
export function trackCtaClick(ctaId, route) {
  try {
    if (isPrerendering || typeof window === 'undefined' || !window.gtag) return;
    window.gtag('event', 'cta_click', {
      cta_id: ctaId,
      page_path: route,
      // Deliberately no `transport_type: 'beacon'` here. That is a legacy Universal
      // Analytics field; GA4's gtag.js already uses navigator.sendBeacon, and passing it
      // just lands an `ep.transport_type=beacon` custom parameter in the payload. Verified
      // in a real browser that the hit survives the immediately-following loginRedirect
      // without it (POST body: `en=cta_click&ep.cta_id=guide_trial:exposed-rdp`).
    });
  } catch (e) {
    // Analytics must never block a conversion. Swallow and continue to the redirect.
  }
}

/**
 * useTrialCta(ctaId) -> startTrial()
 *
 * `startTrial()` sends the visitor to the CIAM **sign-UP** form via
 * `prompt: 'create'`, and preserves the existing `isDevelopment -> /command-center` bypass
 * that every guide's old local handler had.
 *
 * @param {string} [ctaId] identifier for the GA4 event, e.g. 'guide_trial'. Keep it a stable
 *                         slug -- it is the dimension the funnel is read by.
 */
export default function useTrialCta(ctaId = 'guide_trial') {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const isDevelopment = process.env.NODE_ENV === 'development';

  return useCallback(() => {
    // Fire the event BEFORE the redirect: loginRedirect tears down the page, and gtag's
    // transport is best-effort, so anything queued after it can be lost.
    trackCtaClick(ctaId, normalizeRoute(location.pathname));

    if (isDevelopment) {
      navigate('/command-center');
    } else {
      // prompt: 'create' -> the CIAM SIGN-UP form. A cold reader has no account; sending
      // them to sign-in is the conversion defect PRD-193 exists to fix.
      instance.loginRedirect({ ...loginRequest, prompt: 'create' });
    }
  }, [ctaId, location.pathname, isDevelopment, instance, navigate]);
}
